<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy, type ComponentType } from 'svelte';

  import type { FieldState, Option } from '@shared/types';
  import { Chip } from '@shared/ui/chip';
  import { FieldLabel } from '@shared/ui/field-label';
  import { Icon } from '@shared/ui/icon';
  import { Loader } from '@shared/ui/loader';
  import { SearchField } from '@shared/ui/search-field';
  import { is, clickOutside, type SyncOrAsync } from '@shared/utils';

  const dispatch = createEventDispatcher<{ change: Option | Option[]; create: { value: string } }>();

  export let disabled: boolean = false;
  export let loading: boolean = true;
  export let options: SyncOrAsync<Option[]> = [];
  ensurePromiseResolution();
  export let selectedOption: Option | undefined = undefined;

  export let label: string | undefined = undefined;
  export let name: string | undefined = undefined;
  export let isMulti: boolean = false;
  export let isSearchable: boolean = true;
  export let closeOnSelect: boolean = false;
  export let clearOnSelect: boolean = false;
  export let error: string | boolean = false;
  export let state: FieldState = 'default';
  export let selectedOptions: Option[] = [];
  export let optionComponent: ComponentType | undefined = undefined;
  export let hint: string | undefined = undefined;
  export let hasCreateNew: boolean = false;
  export let textCreateNew: string = 'Create New';
  export let placeholder: string = 'Select';
  export let openOnFocus: boolean = false;

  let placement: 'bottom' | 'top' = 'bottom';
  onMount(updatePlacement);

  let searchValue = '';
  let isOpen = false;
  // let isFocused = false; // lint error: unused variable
  let select: HTMLDivElement;
  let dropdown: HTMLDivElement;
  let innerHeight: number;

  function ensurePromiseResolution() {
    if (options instanceof Promise) {
      options.then((res) => {
        options = res;
        loading = false;
      });
    } else {
      loading = false;
    }
  }

  function onSelectHandler(option: Option) {
    if (isMulti) {
      const inSelectedIndex = selectedOptions?.findIndex((s) => s.value === option.value);
      if (inSelectedIndex > -1) {
        removeFromSelected(inSelectedIndex);
      } else {
        selectedOptions = [...selectedOptions, option];
      }
      dispatch('change', selectedOptions);
    } else {
      selectedOption = option;
      isOpen = false;
      dispatch('change', selectedOption);
    }

    if (closeOnSelect) {
      isOpen = false;
    }
  }

  function removeFromSelected(index: number) {
    selectedOptions = [...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)];
    dispatch('change', selectedOptions);
  }

  function updateState(open: boolean) {
    state = open ? 'active' : 'default';
  }

  function getOptions(options: SyncOrAsync<Option[]>, searchValue: string): Option[] {
    if (options instanceof Promise) return [];

    if (!searchValue) return options;
    const filteredOptions = options.filter((item) => item.text.toLowerCase().match(searchValue.toLowerCase()));
    if (!filteredOptions.length && !hasCreateNew) return options;
    return filteredOptions;
  }

  function updatePlacement() {
    const bounding = select.getBoundingClientRect();

    if (bounding.bottom + dropdown.offsetHeight > innerHeight - 100) {
      // 100 is the height of the footer
      placement = 'top';
    } else {
      placement = 'bottom';
    }
  }

  $: updateState(isOpen);
  $: filteredOptions = getOptions(options, searchValue);
  $: isBottomPlacement = placement === 'bottom';

  let searchField: HTMLInputElement;
  let focusTimeout: ReturnType<typeof setTimeout>;

  $: isOpen, handleOpenChange();

  function handleOpenChange() {
    if (isOpen && searchField) {
      focusTimeout = setTimeout(() => {
        searchField.focus();
      }, 100);
    } else {
      clearTimeout(focusTimeout);
    }
  }

  onDestroy(() => {
    clearTimeout(focusTimeout);
  });

  function toggleOpen() {
    if (openOnFocus) return;
    updatePlacement();
    isOpen = !isOpen;
  }

  function isSelected(option: Option) {
    return selectedOptions?.some((s) => s.value === option.value) ?? false;
  }

  function createOption(value: string) {
    dispatch('create', { value });
    isOpen = false;
    searchValue = '';
  }

  function onFocus() {
    // isFocused = true;
    if (openOnFocus && !isOpen) {
      isOpen = true;
    }
  }

  function onBlur() {
    // isFocused = false;
  }
</script>

<svelte:window bind:innerHeight />

<FieldLabel {label} {hint} />

<div
  class="relative w-full"
  bind:this="{select}"
  use:clickOutside="{!isOpen}"
  on:clickOutsideAction="{() => (isOpen = false)}"
>
  <div class="relative">
    <div
      class="rounded-lg border transition-all duration-200
      {is(disabled || loading, 'cursor-not-allowed opacity-55')}
      {is(isOpen, '', '')}
			{is(isOpen && isBottomPlacement, 'rounded-b-none')}
			{is(isOpen && placement !== 'bottom', 'rounded-t-none')}
      {is(
        error,
        'shadow-error  bg-default-100 dark:bg-dark-300 border-red-500',
        'border-default-400 dark:bg-store-dark dark:border-dark-900/20',
      )}
      "
    >
      <button
        disabled="{disabled || loading}"
        type="button"
        class="text-default-900 dark:text-dark-900 relative flex w-full items-center justify-between p-3 pl-4 pr-8 outline-none"
        tabIndex="{0}"
        on:click="{toggleOpen}"
        on:focus="{onFocus}"
        on:blur="{onBlur}"
      >
        {#if clearOnSelect}
          <span class="text-default-500">{placeholder}</span>
        {:else if !selectedOptions.length && !selectedOption?.text}
          <span class="text-default-500">{placeholder}</span>
        {:else if isMulti}
          <div class="-my-1 flex flex-wrap gap-1 px-0 text-sm">
            {#each selectedOptions as option, index}
              <Chip
                label="{option.text}"
                variant="secondary"
                isShowAvatar="{false}"
                size="sm"
                counter="{option.count}"
                onDelete="{() => removeFromSelected(index)}"
              />
            {/each}
          </div>
        {:else}
          <span class="dark:text-white">{selectedOption?.text}</span>
        {/if}

        <div class="text-dark-50 dark:text-dark-900 absolute right-3 flex h-5 w-5 items-center justify-end">
          {#if loading}
            <Loader />
          {:else}
            <Icon name="caretDown" classes="transition-transform duration-200 {is(isOpen, 'rotate-180')}" />
          {/if}
        </div>
      </button>
    </div>

    <div
      bind:this="{dropdown}"
      class="transition-opcity border-default-400 bg-default-50 dark:border-dark-400 dark:bg-dark-200 absolute left-0 z-40
			 flex w-full flex-col
			overflow-hidden border duration-200
			{is(!isOpen, 'invisible opacity-0', 'opacity-1')}
			{is(
        isBottomPlacement,
        'top-full -mt-px rounded-b-lg shadow-[0_4px_15px_0_rgba(0,0,0,0.07)] dark:shadow-[0_4px_15px_0_rgba(0,0,0,0.2)]',
        'bottom-full -mb-px flex-col-reverse rounded-t-lg shadow-[0_-4px_15px_0_rgba(0,0,0,0.07)] dark:shadow-[0_-4px_15px_0_rgba(0,0,0,0.2)]',
      )}"
    >
      {#if isSearchable}
        <SearchField
          {error}
          placeholder="Search..."
          name="search"
          classes="rounded-none border-0"
          bind:value="{searchValue}"
          bind:inputElement="{searchField}"
        />
      {/if}

      <ul
        class="border-default-400 dark:border-dark-400 max-h-56 overflow-y-auto
				{is(isSearchable && isBottomPlacement, 'border-t')}
				{is(isSearchable && !isBottomPlacement, 'border-b')}
				"
      >
        {#each filteredOptions as option}
          <li>
            <button
              disabled="{disabled || loading}"
              type="button"
              class="hover:bg-default-250 hover:text-default-900 dark:hover:bg-dark-350 dark:hover:text-dark-900 focus:bg-default-250 focus:text-default-900 dark:focus:bg-dark-350 dark:focus:text-dark-900 flex w-full items-center
              justify-between gap-2 p-3 text-start focus:outline-none"
              on:click="{() => onSelectHandler(option)}"
              on:keydown="{(e) => e.key === 'Enter' && onSelectHandler(option)}"
            >
              {#if !optionComponent}
                <span>{option.text}</span>
                {#if selectedOption?.value === option.value || isSelected(option)}
                  <span>
                    <Icon name="checkMark" classes="fill-transparent stroke-default-900 dark:stroke-white" />
                  </span>
                {/if}
              {/if}
              {#if optionComponent}
                <svelte:component this="{optionComponent}" {option} />
              {/if}
            </button>
          </li>
        {/each}
      </ul>

      {#if !filteredOptions.length}
        {#if hasCreateNew && searchValue.length >= 2}
          <button
            disabled="{disabled || loading}"
            type="button"
            class="flex w-full items-center gap-3 p-3 px-3.5"
            on:click="{() => createOption(searchValue)}"
            on:keydown="{(e) => e.key === 'Enter' && createOption(searchValue)}"
          >
            <span>
              <Icon name="add" classes="fill-transparent stroke-default-900 dark:stroke-white" />
            </span>
            <span class="text-default-700 text-sm underline hover:no-underline dark:text-white">{textCreateNew}</span>
          </button>
        {:else}
          <p class="text-default-600 dark:text-dark-600 p-3 text-center text-sm">No Options</p>
        {/if}
      {/if}
    </div>
  </div>

  {#if error}
    <p class="mt-2 text-sm text-red-500 first-letter:capitalize">
      {error}
    </p>
  {/if}

  <input type="hidden" {name} value="{JSON.stringify(isMulti ? selectedOptions : selectedOption)}" />
</div>
